import React from "react";
import { Container, Box, Typography, Button, Grid } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useAboutContent } from "../../content/about";
import { useLanguage } from "../../contexts/LanguageContext";
import SEOHead from "../../components/SEOHead";
import Img from "../../components/Img";

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: "easeOut" },
  }),
};

interface Value {
  title: string;
  desc: string;
  image: string;
}

export interface TeamMember {
  name: string;
  qualification?: string;
  role: string;
  image: string;
  desc: string;
  ausbildung?: string[];
  expertise?: string[];
  berufserfahrung?: string[];
  /** Im Admin ausgeblendet */
  hidden?: boolean;
}

export interface AboutContent {
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  intro_label: string;
  intro_title: string;
  intro_text: string;
  intro_image: string;
  values: Value[];
  team: TeamMember[];
  cta_title: string;
  cta_text: string;
}

export const DEFAULT_CONTENT: AboutContent = {
  hero_title: "Erfahren. Mehrsprachig. Persönlich.",
  hero_subtitle:
    "Ihr zuverlässiger Partner für Buchhaltung und Finanzmanagement in Dortmund.",
  hero_image: "/new_images/AdobeStock_236029192.webp",
  intro_label: "Was wir machen",
  intro_title: "TOP Buchhaltungsservice UG",
  intro_text:
    "Wir unterstützen Unternehmen dabei, ihre Buchhaltung klar, digital und zuverlässig zu organisieren. Dabei betreuen wir Mandanten aus vielen Branchen – darunter Bauunternehmen, Apotheken, Ärzte, Gastronomie, Dienstleistungsbetriebe, bilanzierende Unternehmen und weitere Geschäftsbereiche.\n\nUnser Schwerpunkt liegt auf einer strukturierten laufenden Finanzbuchhaltung, der Vorbereitung relevanter Unterlagen sowie der praktischen Unterstützung bei digitalen Buchhaltungsprozessen. Dabei legen wir Wert auf persönliche Betreuung, verständliche Kommunikation und eine effiziente Zusammenarbeit.",
  intro_image: "/new_images/AdobeStock_968935396.webp",
  values: [
    {
      title: "Präzision",
      desc: "Alle buchhalterischen Aufgaben werden mit höchster Genauigkeit und Sorgfalt erledigt.",
      image: "/new_images/AdobeStock_267386573.webp",
    },
    {
      title: "Mehrsprachigkeit",
      desc: "Wir beraten Sie auf Deutsch, Persisch, Englisch und Arabisch.",
      image: "/new_images/AdobeStock_463453443.webp",
    },
    {
      title: "Persönlichkeit",
      desc: "Ein fester Ansprechpartner, der immer für Ihre Fragen da ist.",
      image: "/new_images/AdobeStock_436700764.webp",
    },
    {
      title: "Termintreue",
      desc: "Wir halten Fristen zuverlässig ein – pünktlich und verlässlich.",
      image: "/new_images/pexels-freestockpro-8670381.webp",
    },
  ],
  team: [
    {
      name: "Hasan Al Khdr",
      qualification: "Bachelor in Buchhaltung",
      role: "Geschäftsführer und Gesellschafter der TOP Buchhaltungsservice UG (haftungsbeschränkt)",
      image: "/images/team/hasan-al-khdr.jpg",
      desc: "Geschäftsführer und Gründer von TOP BHS mit Erfahrung im Bereich Finanzbuchhaltung, Steuerrecht und Finanz- und Vermögensberatung.",
      ausbildung: [
        "Bachelorabschluss in Buchhaltung",
        "Umschulung zum Steuerfachangestellten",
        "Ausbildung / Tätigkeit im Bereich Finanz- und Vermögensberatung bei der DVAG",
      ],
      expertise: [
        "Spezialisiert auf die Bearbeitung von Finanzbuchhaltungen sowie auf die Vorbereitung und Mitwirkung bei Steuererklärungen und Jahresabschlüssen im Rahmen der Kanzleitätigkeit.",
        "Fundierte Kenntnisse im deutschen Steuer- und Rechnungswesen.",
        "Erfahrung im Umgang mit Buchhaltungssoftware sowie in der Unterstützung bei buchhalterischen Fragestellungen.",
        "Kenntnisse im Bereich Finanz- und Vermögensberatung, insbesondere in der Analyse individueller Kundenbedürfnisse und der strukturierten Aufbereitung finanzieller Themen.",
        "Mehrsprachige Betreuungskompetenz durch sehr gute Deutschkenntnisse sowie Arabisch als Muttersprache.",
      ],
      berufserfahrung: [
        "Mehr als fünf Jahre Berufserfahrung als Steuerfachangestellter.",
        "Erfahrung im Bereich Finanz- und Vermögensberatung mit Schwerpunkt auf Kundenbetreuung, Analyse individueller Kundenbedürfnisse und strukturierter Aufbereitung finanzieller Themen.",
      ],
    },
    {
      name: "Kai Uwe Wegener-Gärtner",
      qualification: "Diplom-Kaufmann (Univ.)",
      role: "Steuerberater | Gesellschafter der TOP Buchhaltungsservice UG (haftungsbeschränkt)",
      image: "/images/team/kai-uwe-wegener-gaertner.jpg",
      desc: "Steuerberater mit jahrzehntelanger Erfahrung in der Beratung mittelständischer Unternehmen, der öffentlichen Hand und gemeinnütziger Vereine.",
      ausbildung: [
        "Studium an der Universität Osnabrück mit Spezialisierung auf Bilanz-, Steuer- und Prüfungswesen sowie Bankwesen.",
      ],
      expertise: [
        "Spezialisiert auf die ganzheitliche Steuerberatung von mittelständischen Unternehmen.",
        "Besondere Expertise in der Besteuerung der öffentlichen Hand.",
        "Erfahrung in der Beratung von gemeinnützigen Vereinen.",
        "Tätigkeitsschwerpunkte außerdem in energetischen Steuerfragen sowie in der gutachterlichen Tätigkeit.",
        "Gesellschafter der TOP Buchhaltungsservice UG (haftungsbeschränkt). Eine öffentliche Unternehmensdatenbank führt Kai Uwe Wegener-Gärtner als Gesellschafter mit 33 %.",
      ],
      berufserfahrung: [
        "Berufseinstieg in einer mittelständischen Wirtschaftsprüfungs- und Steuerberatungsgesellschaft in Dortmund.",
        "Weitere berufliche Station bei einer internationalen Big-Four-Wirtschaftsprüfungsgesellschaft in Köln.",
        "Langjährige Tätigkeit als Fachbereichsleiter für Steuern in einer mittelständischen Kanzlei.",
        "Mitglied im Steuerberaterverband Westfalen-Lippe.",
        "Zusätzlich tätig als Lehrbeauftragter an verschiedenen Hochschulen.",
        "Engagement als Schatzmeister des Hochschul-Fördervereins Eranus e.V. in Hamm.",
        "Unternehmerische Beteiligung als Gesellschafter der TOP Buchhaltungsservice UG (haftungsbeschränkt).",
      ],
    },
    {
      name: "Rami Abdulkarim",
      qualification: "Bachelor in Buchhaltung",
      role: "Steuerfachangestellter | Fortbildung zum Bilanzbuchhalter | Gesellschafter",
      image: "/images/team/rami-abdulkarim.jpg",
      desc: "Steuerfachangestellter mit fundierter Ausbildung und mehrjähriger Erfahrung in der laufenden Mandantenbetreuung und Finanzbuchhaltung.",
      ausbildung: [
        "Bachelorabschluss in Buchhaltung",
        "Umschulung zum Steuerfachangestellten",
        "Fortbildung zum Bilanzbuchhalter bei Dr. Endriss",
      ],
      expertise: [
        "Spezialisiert auf die Bearbeitung von Finanzbuchhaltungen sowie auf die Vorbereitung und Mitwirkung bei Steuererklärungen und Jahresabschlüssen im Rahmen der Kanzleitätigkeit.",
        "Fundierte Kenntnisse im deutschen Steuer- und Rechnungswesen.",
        "Erfahrung im Umgang mit Buchhaltungssoftware sowie in der Unterstützung bei buchhalterischen Fragestellungen.",
        "Unterstützung bei der Digitalisierung der Finanzbuchhaltung.",
        "Mehrsprachige Betreuungskompetenz durch sehr gute Deutschkenntnisse sowie Arabisch als Muttersprache.",
      ],
      berufserfahrung: [
        "Mehr als fünf Jahre Berufserfahrung als Steuerfachangestellter.",
        "Eigenständige laufende Mandantenbetreuung im Rahmen der Kanzleiorganisation.",
        "Bearbeitung von Finanzbuchhaltungen sowie vorbereitende Tätigkeiten im Bereich Steuererklärungen und Jahresabschlüsse.",
      ],
    },
  ],
  cta_title: "Haben Sie Fragen?",
  cta_text: "Sprechen Sie uns an – wir sind für Sie da.",
};

export function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((p) => p.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const TeamCard: React.FC<{ member: TeamMember; index: number }> = ({
  member,
  index,
}) => {
  const [hovered, setHovered] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => { setImgError(false); }, [member.image]);
  const slug = toSlug(member.name);
  const showInitials = !member.image || imgError;

  return (
    <MotionBox
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={index}
      variants={fadeUp}
    >
      <RouterLink
        to={`/ueber-uns/team/${slug}`}
        state={{ member }}
        style={{ textDecoration: "none", display: "block" }}
      >
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            position: "relative",
            borderRadius: "12px",
            overflow: "hidden",
            height: { xs: 380, md: 460 },
            cursor: "pointer",
            boxShadow: hovered
              ? "0 24px 60px rgba(0,0,0,0.18)"
              : "0 4px 20px rgba(0,0,0,0.08)",
            transition: "box-shadow 0.35s ease",
          }}
        >
          {/* Photo or initials */}
          {showInitials ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: hovered ? "#0f2899" : "#1737bc",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                transition: "background-color 0.35s ease",
              }}
            >
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "2px solid rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 800,
                    color: "rgba(255,255,255,0.75)",
                    letterSpacing: "-1px",
                  }}
                >
                  {getInitials(member.name)}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "rgba(255,255,255,0.35)",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  mt: 1,
                }}
              >
                Foto folgt
              </Typography>
            </Box>
          ) : (
            <Img
              src={member.image}
              alt={member.name}
              width={600}
              height={750}
              loading="lazy"
              decoding="async"
              onError={() => setImgError(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "top center",
                display: "block",
                transform: hovered ? "scale(1.05)" : "scale(1)",
                transition: "transform 0.5s ease",
              }}
            />
          )}

          {/* Bottom overlay */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: hovered
                ? "linear-gradient(to top, rgba(10,10,26,0.96) 0%, rgba(10,10,26,0.7) 60%, transparent 100%)"
                : "linear-gradient(to top, rgba(10,10,26,0.88) 0%, rgba(10,10,26,0.4) 55%, transparent 100%)",
              p: { xs: 3, md: 4 },
              transition: "background 0.35s ease",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "#fff",
                mb: 0.25,
              }}
            >
              {member.name}
            </Typography>
            {member.qualification && (
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "#818cf8",
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {member.qualification}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "rgba(255,255,255,0.55)",
                mb: hovered ? 2 : 0,
                transition: "margin 0.3s ease",
                lineHeight: 1.4,
              }}
            >
              {member.role}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#818cf8",
                fontSize: "0.82rem",
                fontWeight: 600,
                opacity: hovered ? 1 : 0,
                transform: hovered ? "translateY(0)" : "translateY(6px)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
            >
              Profil ansehen
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        </Box>
      </RouterLink>
    </MotionBox>
  );
};

const UeberUnsPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const content = useAboutContent(currentLanguage, DEFAULT_CONTENT) ?? DEFAULT_CONTENT;

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <SEOHead
        title={t("seo.ueber_uns_title")}
        description={t("seo.ueber_uns_description")}
        canonicalPath="/ueber-uns"
      />
      {/* ── Hero ── */}
      <Box
        sx={{
          position: "relative",
          backgroundImage: `url(${content.hero_image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden",
          pt: { xs: 14, md: 16 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(10,10,26,0.82) 0%, rgba(10,10,26,0.55) 100%)",
          }}
        />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <MotionBox
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeUp}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.3)",
                borderRadius: "100px",
                px: 2.5,
                py: 0.75,
                mb: 4,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "#818cf8" }} />
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  color: "#818cf8",
                  fontWeight: 600,
                }}
              >
                Über uns
              </Typography>
            </Box>
          </MotionBox>
          <MotionBox
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.8rem", md: "4.5rem" },
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.1,
                mb: 3,
                maxWidth: 700,
              }}
            >
              {content.hero_title}
            </Typography>
          </MotionBox>
          <MotionBox
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.5)",
                fontSize: "1.1rem",
                maxWidth: 500,
                lineHeight: 1.8,
              }}
            >
              {content.hero_subtitle}
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ── Team Foto + Was wir machen ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          variants={fadeUp}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 10 },
            alignItems: { md: "center" },
          }}
        >
          {/* Group photo */}
          <Box
            sx={{
              flex: 1,
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
              minHeight: { xs: 260, md: 420 },
              backgroundImage: `url(${content.intro_image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              order: { xs: 0, md: 0 },
            }}
          />
          {/* Was wir machen */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: "#1737bc",
                letterSpacing: "3px",
                fontWeight: 700,
                display: "block",
                mb: 2,
              }}
            >
              {content.intro_label}
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.8rem", md: "2.4rem" },
                color: "#0a0a1a",
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              {content.intro_title}
            </Typography>
            {content.intro_text.split("\n\n").map((para, i) => (
              <Typography
                key={i}
                sx={{ color: "#444", fontSize: "1rem", lineHeight: 1.9, mb: 2 }}
              >
                {para}
              </Typography>
            ))}
          </Box>
        </MotionBox>
      </Container>

      {/* ── Team Cards ── */}
      {content.team && content.team.length > 0 && (
        <Box sx={{ bgcolor: "#f8f8f8", py: { xs: 8, md: 12 } }}>
          <Container maxWidth="lg">
            <MotionBox
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
              variants={fadeUp}
              sx={{ mb: 7, textAlign: "center" }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: "#1737bc",
                  letterSpacing: "3px",
                  fontWeight: 700,
                  display: "block",
                  mb: 2,
                }}
              >
                Unser Team
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.8rem", md: "2.4rem" },
                  color: "#0a0a1a",
                  lineHeight: 1.2,
                }}
              >
                Die Menschen hinter TOP BHS
              </Typography>
            </MotionBox>

            <Grid container spacing={3}>
              {content.team.map((member, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <TeamCard member={member} index={i} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* ── CTA Banner ── */}
      <Box sx={{ bgcolor: "#0a0a1a", py: 10, textAlign: "center", px: 3 }}>
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          variants={fadeUp}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              mb: 2,
            }}
          >
            {content.cta_title}
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.6)", fontSize: "1rem", mb: 4 }}
          >
            {content.cta_text}
          </Typography>
          <Button
            variant="contained"
            href="/contact"
            sx={{
              bgcolor: "#ffffff",
              color: "#0a0a1a",
              fontWeight: 700,
              fontSize: "0.95rem",
              px: 4,
              py: 1.5,
              borderRadius: "8px",
              "&:hover": { bgcolor: "#f0f0f0" },
            }}
          >
            Kontakt aufnehmen
          </Button>
        </MotionBox>
      </Box>
    </Box>
  );
};

export default UeberUnsPage;
